import React, { useCallback, useContext, useEffect } from 'react';
import { BreakpointTrackerContext } from '@jutro/layout';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import _ from 'lodash';
import { DatatableUtil } from '@xengage/gw-portals-util-js';
import { useTranslator } from '@jutro/locale';
import { UWIssuesUtil } from "premier-policycommon-util-js";
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import PrintDocumentsComponent from "premier-capability-policyjob-react/components/PrintDocumentsComponent/PrintDocumentsComponent";
import FormsComponent from "premier-capability-policyjob-react/components/FormComponent/FormComponent";
import QuoteValidityComponent from "premier-capability-policyjob-react/components/QuoteValidityComponent/QuoteValidityComponent";
import QuoteDetailsComponent from 'premier-capability-policyjob-react/components/QuoteDetailsComponent/QuoteDetailsComponent';
import presentationMetadata from './QuotePage.metadata.json5';
import styles from './QuotePage.module.scss';
import messages from './QuotePage.messages';
import CoveragesPageComponent from "../Coverages/CoveragesPage";

function QuotePage(props) {
    const { authHeader } = useAuthentication();
    const {
        wizardData: submissionVM,
        steps,
        jumpTo,
        currentStep,
        wizardData, updateWizardData,
        underwritingIssues
    } = props;


    const breakpoint = useContext(BreakpointTrackerContext);
    const { LoadSaveService } = useDependencies('LoadSaveService');
    const { pageKey } = currentStep;
    const isQuoteBlocked = UWIssuesUtil.isQuoteBlocked(underwritingIssues);
    const findBlockingPoint = UWIssuesUtil.findBlockingPoint(underwritingIssues);
    const underWritingIssuesNonBlocking = UWIssuesUtil.UWIssuesNonBlocking(underwritingIssues);
    const translator = useTranslator();


    const metadata = presentationMetadata['SpeLinProduct.default.quote'];

    const handleNext = useCallback(() => {
        const offeredQuotes = _.get(submissionVM.value, 'quoteData.offeredQuotes[0].publicID');

        _.set(submissionVM, 'bindData.chosenQuote', offeredQuotes);

        return submissionVM;
    }, [submissionVM]);

    const navigateToCoverages = useCallback(() => {

        const selectOffering = submissionVM.value.lobData.speLinLine.selectedOffering_Ext
        let redirectPath = ''

        switch (selectOffering) {
            case 'Hol':
                redirectPath = '/HoleInOne';
                break;
            case 'Sel':
                redirectPath = '/SpelEventLiability';
                break;
            default:
                break;
        }

        const indexOfGeneralCoverages = _.findIndex(
            steps,
            ({ path }) => path === redirectPath
        );

        jumpTo(indexOfGeneralCoverages);
    }, [jumpTo, steps, submissionVM]);

    // eslint-disable-next-line arrow-body-style
    const getCell = (item, _index, property) => {
        return item[property.id];
    };

    const getFormTableData = () => _.map(submissionVM?.value?.lobData?.speLinLine?.lineCoverages?.forms_Ext, (form) => ({
        formDes: form.formDes,
        formCategory: form.formCategory
    }));

    const getInternalUseOnlyConditionsOfQuoteTableData = () => _.map(_.get(submissionVM.value, 'lobData.speLinLine.coverables.internalUse.subjectConditions'), (pctsubofQuoExp) => ({
        conFieConditionsOfQuote: pctsubofQuoExp?.conditionName
    }));

    const getInternalUseOnlyAdditionalConditionsTableData = () => _.map(_.get(submissionVM.value, 'lobData.speLinLine.coverables.internalUse.additionalConditions'), (pctaddConofPolExp) => ({
        conFieAdditionalConditions: pctaddConofPolExp?.conditionName
    }));



    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            navigateToCoverages,
            getCell,
            sortString: DatatableUtil.sortString
        },
        resolveComponentMap: {
            coveragespagecomponent: CoveragesPageComponent,
            quoteDetailsComponent: QuoteDetailsComponent,
            quoteValidityComponent: QuoteValidityComponent,
            PrintDocumentsComponent,
            FormsComponent
        }
    };

    const generatelocRiskseOverrides = () => {
        const overrides = submissionVM.value.lobData.speLinLine?.coverables?.locRisks?.map((locRisk, index) => ({
            [`locRiskSection${index}`]: {
                title: locRisk.accountLocFie.displayName
            },
            [`locRiskCoverageInfoComponent${index}`]: {
                wizardData,
                updateWizardData,
                clausesBasePath: `lobData.speLinLine.lineCoverages.locRiskCoverages.children[${index}].coverages`,
                title: "Coverages",
                clasueType: 'coverages',
                isEditable: false
            }
        }));

        if (overrides) {
            return Object.assign({}, ...overrides);
        }

        return {}
    };


    const overrideProps = {
        '@field': {
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
            readOnly: true
        },
        underWritingIssues: {
            underwritingIssues: underWritingIssuesNonBlocking,
            blockQuote: findBlockingPoint,
            filterUWIssuesInCustomOffering: false,
            quoteID: _.get(submissionVM.value, 'quoteID'),
            accountNumber: _.get(submissionVM.value, 'baseData.accountNumber')
        },
        liabilityCoverageAccordion: {
            isEditable: false,
            readOnly: true
        },
        liabilityCoverageInfoComponent: {
            wizardData,
            updateWizardData,
            clausesBasePath: `lobData.speLinLine.lineCoverages.coverages`,
            title: messages.covAccordion,
            clasueType: 'coverages',
            isEditable: false,
            // pathToGet: 'speLinLine.coverages'
        },
        quoteDetailsComponent: {
            wizardData
        },
        quoteValidityComponent: {
            wizardData
        },
        policyQuoteValidityDays: {
            content: _.get(submissionVM, `baseData.quoteValidityDays_Ext.value`)
        },
        policyQuoteValDaysHead: {
            content: translator(messages.scQuoteValidityDays, {
                quoteValidityDays: _.get(submissionVM, `baseData.quoteValidityDays_Ext.value`)
            })
        },
        tableFormSummary: {
            data: getFormTableData()
        },
        tableInternalUseOnlyConditionsOfQuote: {
            data: getInternalUseOnlyConditionsOfQuoteTableData()
        },
        tableInternalUseOnlyAdditionalConditions: {
            data: getInternalUseOnlyAdditionalConditionsTableData()
        },
        internalUseOnlyAccordion: {
            visible: _.get(submissionVM, 'lobData.speLinLine.selectedOffering_Ext.value') === 'Hol'
                && (_.get(submissionVM, 'lobData.speLinLine.coverables.internalUse.subjectConditions')?.length > 0
                    || _.get(submissionVM.value, 'lobData.speLinLine.coverables.internalUse.additionalConditions')?.length > 0)
        },
        internalUseOnlyConditionsOfQuoteContainer: {
            visible: _.get(submissionVM, 'lobData.speLinLine.selectedOffering_Ext.value') === 'Hol'
                && _.get(submissionVM.value, 'lobData.speLinLine.coverables.internalUse.subjectConditions')?.length > 0
        },
        internalUseOnlyAdditionalConditionsContainer: {
            visible: _.get(submissionVM, 'lobData.speLinLine.selectedOffering_Ext.value') === 'Hol'
                && _.get(submissionVM.value, 'lobData.speLinLine.coverables.internalUse.additionalConditions')?.length > 0
        },
        taxes: {
            visible: _.get(submissionVM.value, 'baseData.baseState_Ext') === 'QC',
        },
        fees: {
            visible: _.get(submissionVM.value, 'quoteData.offeredQuotes') &&
                _.get(submissionVM.value, 'quoteData.offeredQuotes[0].premium.fees.amount') !== 0
        },
        policyfees: {
            visible: _.get(submissionVM.value, 'baseData.policyFee_Ext.amount') !== 0
        },
        inspectionFee: {
            visible: _.get(submissionVM.value, 'baseData.inspectionFee_Ext.amount') !== 0
        },
        extensionFee: {
            visible: _.get(submissionVM.value, 'baseData.extensionFee_Ext.amount') !== 0
        },
        PrintDocumentsComponent: {
            visible: submissionVM.value.baseData.periodStatus === 'Quoted',
            jobNumber: submissionVM.value.quoteID,
            authHeader
        },
        FormsComponent: {
            forms: submissionVM.value.lobData.speLinLine.lineCoverages.forms_Ext
        },
        ...generatelocRiskseOverrides()
    };

    useEffect(() => { }, []);

    return (
        <WizardPage
            disableNext={underWritingIssuesNonBlocking?.length > 0}
            onNext={handleNext}>
            <ViewModelForm
                uiProps={metadata.content}
                model={submissionVM.value}
                overrideProps={overrideProps}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
                componentMap={resolvers.resolveComponentMap}
            />
        </WizardPage>
    );
}

QuotePage.propTypes = wizardProps;
export default QuotePage;
